export type UpstreamRequest = {
  method: string;
  path: string;
  domain: string;
  url?: string;
  request_id?: string;
  bytes?: number;
};

/** Query values never define an endpoint; UUIDs and numeric record ids do not either. */
export function upstreamPathTemplate(path: string): string {
  return path
    .split(/[?#]/, 1)[0]
    .replace(
      /\/(?:[0-9]+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?=\/|$)/gi,
      '/:id'
    )
    .replace(/\/$/, '');
}

export function upstreamRequest(
  path: string,
  domain: string,
  init?: RequestInit
): UpstreamRequest {
  return {
    method: (init?.method ?? 'GET').toUpperCase(),
    path: upstreamPathTemplate(path),
    domain,
    url: path.split(/[?#]/, 1)[0],
  };
}

export class UpstreamTransportError extends Error {
  readonly timedOut: boolean;

  constructor(
    readonly upstream: string,
    readonly request: UpstreamRequest,
    cause: unknown
  ) {
    const timedOut =
      (cause instanceof Error || cause instanceof DOMException) &&
      cause.name === 'TimeoutError';
    const kind = timedOut ? 'timed out' : 'failed';
    super(
      `${upstream} ${request.method} ${request.path} ${kind} (default timeout 15000ms)`,
      { cause }
    );
    this.name = 'UpstreamTransportError';
    this.timedOut = timedOut;
  }
}

/**
 * A failed response's size and the upstream's request id, never its body: the
 * body can quote the input it rejected, and the request id finds the upstream's
 * own log line, which holds the rest.
 */
export function respondedRequest(
  res: Pick<Response, 'headers'>,
  body: string,
  request: UpstreamRequest
): UpstreamRequest {
  return {
    ...request,
    request_id: res.headers.get('x-request-id') ?? 'none',
    bytes: new TextEncoder().encode(body).byteLength,
  };
}

/** A non-OK response from an upstream other than the monolith. */
export class UpstreamResponseError extends Error {
  constructor(
    readonly upstream: string,
    readonly httpStatus: number,
    readonly httpBody: string,
    readonly request: UpstreamRequest
  ) {
    super(
      `${upstream} ${request.method} ${request.path} returned ${httpStatus}`
    );
    this.name = 'UpstreamResponseError';
  }
}

/** A fetch can receive headers before its body times out or the connection closes. */
export async function readUpstreamResponse(
  res: Response,
  parseJson: boolean | 'arrayBuffer',
  upstream: string,
  request: UpstreamRequest
) {
  try {
    if (parseJson === 'arrayBuffer') {
      return await res.arrayBuffer();
    }
    return await (parseJson ? res.json() : res.text());
  } catch (cause) {
    if (
      (cause instanceof Error || cause instanceof DOMException) &&
      (cause.name === 'TimeoutError' ||
        cause.name === 'AbortError' ||
        cause.name === 'TypeError')
    ) {
      throw new UpstreamTransportError(upstream, request, cause);
    }
    throw cause;
  }
}

export async function fetchWithRequestContext(
  url: string,
  init: RequestInit,
  upstream: string,
  request: UpstreamRequest
): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (cause) {
    throw new UpstreamTransportError(upstream, request, cause);
  }
}
